import React from "react";
import { useSearchParams, useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { api, inr, isOnSale } from "@/lib/api";
import { colorToCss } from "@/lib/colors";
import { ProductCard, SkeletonCard } from "@/components/ProductCard";
import { SlidersHorizontal, X, Search, ChevronDown, ArrowDownUp } from "lucide-react";
import { Pagination, usePaged } from "@/components/Pagination";

const PER_PAGE = 12;

const SORTS = [
  { v: "newest", label: "Newest" },
  { v: "price_asc", label: "Lowest price" },
  { v: "price_desc", label: "Highest price" },
  { v: "name", label: "Alphabetical" },
];
const TAGS = [
  { v: "sale", label: "On Sale" },
  { v: "featured", label: "Featured" },
  { v: "instock", label: "In stock" },
];
const swatchFor = (name) => colorToCss(name) || "#d4d4d4";

const listParam = (params, k) => (params.get(k) || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function Shop() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";
  const colors = listParam(params, "color");
  const fabrics = listParam(params, "fabric");
  const occasions = listParam(params, "occasion");
  const tags = listParam(params, "tags");
  const minPrice = params.get("min_price") ? Number(params.get("min_price")) : null;
  const maxPrice = params.get("max_price") ? Number(params.get("max_price")) : null;

  const [sidebar, setSidebar] = React.useState(true);   // desktop show/hide
  const [sheet, setSheet] = React.useState(false);      // mobile bottom sheet
  const searchRef = React.useRef(null);      // mobile/tablet search box
  const deskSearchRef = React.useRef(null);  // desktop sidebar search box
  React.useEffect(() => {
    if (params.get("focus") !== "search") return;
    const el = [deskSearchRef.current, searchRef.current].find((x) => x && x.offsetParent !== null);
    el?.focus();
  }, [params]);
  React.useEffect(() => {
    document.body.style.overflow = sheet ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sheet]);

  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const { data: all, isLoading } = useQuery({
    queryKey: ["shop-all"],
    queryFn: () => api.get("/products?limit=500&sort=newest").then((r) => r.data),
    staleTime: 60 * 1000,
  });

  const activeCat = (cats || []).find((c) => c.slug === slug);
  const parentCats = (cats || []).filter((c) => !c.parent_id);
  const subCats = (cats || []).filter((c) => c.parent_id && c.parent_id === (activeCat?.parent_id || activeCat?.id));
  const catIdsFor = React.useCallback((c) => [c.id, ...(cats || []).filter((x) => x.parent_id === c.id).map((x) => x.id)], [cats]);

  // 1) products in the selected category (+ its sub-categories) and matching the search text
  const inCategory = React.useMemo(() => {
    let list = all || [];
    if (activeCat) { const ids = new Set(catIdsFor(activeCat)); list = list.filter((p) => ids.has(p.category_id)); }
    if (q) {
      const t = q.toLowerCase();
      list = list.filter((p) => [p.name, p.description, p.fabric, p.color, p.occasion, p.weave].some((v) => (v || "").toLowerCase().includes(t)));
    }
    return list;
  }, [all, activeCat, catIdsFor, q]);

  const priceBounds = React.useMemo(() => {
    const prices = inCategory.map((p) => Number(p.price) || 0);
    if (!prices.length) return [0, 1000];
    const step = 500;
    let lo = Math.floor(Math.min(...prices) / step) * step;
    let hi = Math.ceil(Math.max(...prices) / step) * step;
    if (hi <= lo) { lo = Math.max(0, lo - step); hi = hi + step; }  // one price only — still show the bar
    return [lo, hi];
  }, [inCategory]);

  // 2) apply facet filters
  const matches = React.useCallback((p, skip) => {
    if (skip !== "color" && colors.length && !colors.some((c) => (p.color || "").toLowerCase() === c.toLowerCase())) return false;
    if (skip !== "fabric" && fabrics.length && !fabrics.some((c) => (p.fabric || "").toLowerCase() === c.toLowerCase())) return false;
    if (skip !== "occasion" && occasions.length && !occasions.some((c) => (p.occasion || "").toLowerCase() === c.toLowerCase())) return false;
    if (skip !== "price") {
      if (minPrice != null && Number(p.price) < minPrice) return false;
      if (maxPrice != null && Number(p.price) > maxPrice) return false;
    }
    if (skip !== "tags") {
      if (tags.includes("sale") && !isOnSale(p)) return false;
      if (tags.includes("featured") && !p.featured) return false;
      if (tags.includes("instock") && !(p.stock > 0)) return false;
    }
    return true;
  }, [colors, fabrics, occasions, minPrice, maxPrice, tags]);

  const results = React.useMemo(() => {
    const list = inCategory.filter((p) => matches(p));
    const by = {
      newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      price_asc: (a, b) => a.price - b.price,
      price_desc: (a, b) => b.price - a.price,
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
    }[sort] || (() => 0);
    return [...list].sort(by);
  }, [inCategory, matches, sort]);

  // 12 products per page; back to page 1 whenever the category, search, filters or sort change
  const pg = usePaged(results, PER_PAGE, { resetKey: `${slug || ""}?${params.toString()}`, scrollTop: true });

  // facet options with counts (each facet counted with the *other* filters applied)
  const facet = (key) => {
    const counts = new Map();
    inCategory.filter((p) => matches(p, key)).forEach((p) => {
      const v = (p[key] || "").trim();
      if (v) counts.set(v, (counts.get(v) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  };
  const catCount = (c) => { const ids = new Set(catIdsFor(c)); return (all || []).filter((p) => ids.has(p.category_id)).length; };

  // URL helpers
  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    p.delete("focus");
    if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) p.delete(k);
    else p.set(k, Array.isArray(v) ? v.join(",") : String(v));
    setParams(p, { replace: true });
  };
  const toggleIn = (k, list, v) => setParam(k, list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const clearAll = () => {
    const p = new URLSearchParams();
    if (sort !== "newest") p.set("sort", sort);
    setParams(p, { replace: true });
  };

  const chips = [
    ...(q ? [{ label: `“${q}”`, clear: () => setParam("q", "") }] : []),
    ...tags.map((t) => ({ label: TAGS.find((x) => x.v === t)?.label || t, clear: () => toggleIn("tags", tags, t) })),
    ...colors.map((c) => ({ label: c, clear: () => toggleIn("color", colors, c) })),
    ...fabrics.map((c) => ({ label: c, clear: () => toggleIn("fabric", fabrics, c) })),
    ...occasions.map((c) => ({ label: c, clear: () => toggleIn("occasion", occasions, c) })),
    ...(minPrice != null || maxPrice != null
      ? [{ label: minPrice != null ? `${inr(minPrice)} – ${inr(maxPrice ?? priceBounds[1])}` : `Up to ${inr(maxPrice)}`, clear: () => { const p = new URLSearchParams(params); p.delete("min_price"); p.delete("max_price"); setParams(p, { replace: true }); } }]
      : []),
  ];

  const filterPanel = (
    <FilterPanel
      parentCats={parentCats} cats={cats || []} activeCat={activeCat} catCount={catCount} totalCount={(all || []).length}
      onCategory={(s) => { setSheet(false); nav(`/shop${s ? `/${s}` : ""}${params.toString() ? `?${params.toString()}` : ""}`); }}
      priceBounds={priceBounds} minPrice={minPrice} maxPrice={maxPrice}
      onPrice={(lo, hi) => {
        const p = new URLSearchParams(params);
        lo > priceBounds[0] ? p.set("min_price", lo) : p.delete("min_price");
        hi < priceBounds[1] ? p.set("max_price", hi) : p.delete("max_price");
        setParams(p, { replace: true });
      }}
      colors={facet("color")} selColors={colors} onColor={(v) => toggleIn("color", colors, v)} clearColors={() => setParam("color", "")}
      fabrics={facet("fabric")} selFabrics={fabrics} onFabric={(v) => toggleIn("fabric", fabrics, v)} clearFabrics={() => setParam("fabric", "")}
      occasions={facet("occasion")} selOccasions={occasions} onOccasion={(v) => toggleIn("occasion", occasions, v)}
      tags={tags} onTag={(v) => toggleIn("tags", tags, v)}
    />
  );

  const title = activeCat?.name || (q ? "Search results" : "All Pieces");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      {/* Heading */}
      <div className="mb-6 sm:mb-8">
        <div className="text-xs text-neutral-500 mb-3 flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:text-[#0E4D34]">Home</Link><span>/</span>
          <Link to="/shop" className={activeCat ? "hover:text-[#0E4D34]" : "text-neutral-400"}>Shop</Link>
          {activeCat && <><span>/</span><span className="text-neutral-400">{activeCat.name}</span></>}
        </div>
        <h1 className="h1-editorial font-serif">{title}</h1>
        {activeCat?.description && <p className="text-neutral-600 mt-3 max-w-2xl text-sm sm:text-base">{activeCat.description}</p>}
      </div>

      {subCats.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-6 -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
          {activeCat?.parent_id && (
            <Link to={`/shop/${(cats || []).find((c) => c.id === activeCat.parent_id)?.slug || ""}`}
                  className="text-xs px-4 py-2 rounded-full border border-neutral-300 hover:border-[#0E4D34] whitespace-nowrap">All {(cats || []).find((c) => c.id === activeCat.parent_id)?.name}</Link>
          )}
          {subCats.map((sc) => (
            <Link key={sc.id} to={`/shop/${sc.slug}`} data-testid={`subcat-${sc.slug}`}
                  className={`text-xs px-4 py-2 rounded-full border whitespace-nowrap transition-colors ${sc.slug === slug ? "bg-[#0E4D34] text-white border-[#0E4D34]" : "border-neutral-300 hover:border-[#0E4D34]"}`}>{sc.name}</Link>
          ))}
        </div>
      )}

      {/* Mobile / tablet controls */}
      <div className="lg:hidden space-y-4 mb-6">
        <SearchBox inputRef={searchRef} value={q} onSubmit={(v) => setParam("q", v)}/>
        <button onClick={() => setSheet(true)} data-testid="btn-filters"
                className="w-full rounded-full border border-neutral-300 bg-white py-3.5 text-sm font-semibold flex items-center justify-center gap-2 hover:border-[#0E4D34]">
          <SlidersHorizontal size={15}/> Show filters{chips.length ? ` (${chips.length})` : ""}
        </button>
        <div className="text-sm text-neutral-600" data-testid="result-count">Showing {results.length} of {inCategory.length}</div>
        {chips.length > 0 && <Chips chips={chips} onClear={clearAll}/>}
        <div>
          <div className="text-xs text-neutral-500 mb-1.5">Sort by</div>
          <SortSelect value={sort} onChange={(v) => setParam("sort", v === "newest" ? "" : v)} full/>
        </div>
      </div>

      <div className={`lg:grid gap-8 xl:gap-10 ${sidebar ? "lg:grid-cols-[250px_1fr]" : "lg:grid-cols-1"}`}>
        {/* Desktop sidebar */}
        {sidebar && (
          <aside className="hidden lg:block">
            <div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto custom-scroll pr-2 pb-6">
              <button onClick={() => setSidebar(false)} data-testid="btn-hide-filters"
                      className="w-full rounded-full border border-neutral-300 py-2 text-xs font-semibold mb-6 hover:border-[#0E4D34]">Hide filters</button>
              <div className="mb-6"><SearchBox inputRef={deskSearchRef} value={q} onSubmit={(v) => setParam("q", v)}/></div>
              {filterPanel}
            </div>
          </aside>
        )}

        <div>
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center gap-3 mb-6 min-h-[40px]">
            {!sidebar && (
              <button onClick={() => setSidebar(true)} className="rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold flex items-center gap-2 hover:border-[#0E4D34]">
                <SlidersHorizontal size={13}/> Show filters
              </button>
            )}
            <div className="text-sm text-neutral-600 whitespace-nowrap" data-testid="result-count-desktop">Showing {results.length} of {inCategory.length}</div>
            <div className="flex-1 min-w-0">{chips.length > 0 && <Chips chips={chips} onClear={clearAll}/>}</div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-neutral-500">Sort by</span>
              <SortSelect value={sort} onChange={(v) => setParam("sort", v === "newest" ? "" : v)}/>
            </div>
          </div>

          <div className={`grid grid-cols-2 md:grid-cols-3 ${sidebar ? "xl:grid-cols-3" : "lg:grid-cols-4"} gap-x-4 gap-y-8 sm:gap-x-6 lg:gap-x-7`}>
            {isLoading ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i}/>) :
              results.length === 0 ? (
                <div className="col-span-full text-center py-16 bg-white rounded-2xl border">
                  <div className="font-serif text-2xl mb-2">No pieces match</div>
                  <p className="text-sm text-neutral-500 mb-5">Try removing a filter or searching for something else.</p>
                  {chips.length > 0 && <button onClick={clearAll} className="btn-outline-silk rounded-full">Clear all filters</button>}
                </div>
              ) : pg.items.map((p, i) => <ProductCard key={p.id} product={p} index={i}/>)}
          </div>
          {!isLoading && <Pagination {...pg.props} label="pieces"/>}
        </div>
      </div>

      {/* Mobile bottom sheet */}
      <AnimatePresence>
        {sheet && (
          <>
            <motion.div className="fixed inset-0 z-[60] bg-black/40 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSheet(false)}/>
            <motion.div className="fixed inset-x-0 bottom-0 z-[61] bg-white rounded-t-3xl max-h-[88vh] flex flex-col lg:hidden" data-testid="filter-sheet"
                        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "tween", duration: 0.28 }}>
              <div className="flex justify-center pt-2.5"><span className="w-10 h-1 rounded-full bg-neutral-300"/></div>
              <div className="flex items-center justify-between px-5 py-3 border-b">
                <h3 className="font-semibold">Filters</h3>
                <button onClick={() => setSheet(false)} aria-label="Close filters" className="p-2 rounded-full hover:bg-neutral-100"><X size={20}/></button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-5">{filterPanel}</div>
              <div className="border-t p-4 grid grid-cols-2 gap-3">
                <button onClick={clearAll} className="rounded-full border border-neutral-300 py-3 text-sm font-semibold">Clear all</button>
                <button onClick={() => setSheet(false)} className="rounded-full bg-[#0E4D34] text-white py-3 text-sm font-semibold" data-testid="sheet-apply">Show {results.length} results</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------------- pieces ---------------- */
function SearchBox({ value, onSubmit, inputRef }) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => setV(value), [value]);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(v.trim()); }}
          className="flex items-center gap-2 rounded-full border border-neutral-300 bg-white px-4 py-2.5 focus-within:border-[#0E4D34]">
      <Search size={15} className="text-neutral-500 shrink-0"/>
      <input ref={inputRef} value={v} onChange={(e) => setV(e.target.value)} placeholder="Search pieces…" data-testid="shop-search"
             className="flex-1 min-w-0 text-sm bg-transparent outline-none"/>
      {v && <button type="button" onClick={() => { setV(""); onSubmit(""); }} aria-label="Clear search"><X size={14} className="text-neutral-400"/></button>}
    </form>
  );
}

function SortSelect({ value, onChange, full }) {
  return (
    <div className={`relative ${full ? "w-full" : ""}`}>
      <ArrowDownUp size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"/>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="sort-select"
              className={`appearance-none rounded-full border border-neutral-300 bg-white pl-10 pr-10 text-sm focus:outline-none focus:border-[#0E4D34] cursor-pointer ${full ? "w-full py-3.5" : "py-2"}`}>
        {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
      </select>
      <ChevronDown size={15} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"/>
    </div>
  );
}

function Chips({ chips, onClear }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((c, i) => (
        <button key={i} onClick={c.clear} data-testid="filter-chip"
                className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white pl-3.5 pr-2.5 py-1.5 text-xs hover:border-[#991B1B] hover:text-[#991B1B]">
          {c.label}<X size={12}/>
        </button>
      ))}
      <button onClick={onClear} className="text-xs text-neutral-500 underline underline-offset-2 hover:text-[#991B1B] px-1">Clear all</button>
    </div>
  );
}

function Section({ title, onClear, children }) {
  return (
    <div className="mb-7">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-neutral-900">{title}</h4>
        {onClear && <button onClick={onClear} className="text-[11px] text-neutral-400 hover:text-[#991B1B]">Clear</button>}
      </div>
      {children}
    </div>
  );
}

function FilterPanel(props) {
  const { parentCats, cats, activeCat, catCount, totalCount, onCategory, priceBounds, minPrice, maxPrice, onPrice,
    colors, selColors, onColor, clearColors, fabrics, selFabrics, onFabric, clearFabrics, occasions, selOccasions, onOccasion, tags, onTag } = props;
  const activeParentId = activeCat ? (activeCat.parent_id || activeCat.id) : null;

  return (
    <div className="text-sm">
      <Section title="Categories">
        <ul className="space-y-1">
          <CatRow label="All pieces" count={totalCount} active={!activeCat} onClick={() => onCategory("")}/>
          {parentCats.map((c) => (
            <React.Fragment key={c.id}>
              <CatRow label={c.name} count={catCount(c)} active={activeCat?.id === c.id} onClick={() => onCategory(c.slug)} testid={`cat-${c.slug}`}/>
              {activeParentId === c.id && cats.filter((s) => s.parent_id === c.id).map((s) => (
                <CatRow key={s.id} sub label={s.name} count={catCount(s)} active={activeCat?.id === s.id} onClick={() => onCategory(s.slug)}/>
              ))}
            </React.Fragment>
          ))}
        </ul>
      </Section>

      <Section title="Price" onClear={minPrice != null || maxPrice != null ? () => onPrice(priceBounds[0], priceBounds[1]) : null}>
        <PriceRange bounds={priceBounds} value={maxPrice ?? priceBounds[1]} onCommit={(hi) => onPrice(priceBounds[0], hi)}/>
      </Section>

      {colors.length > 0 && (
        <Section title="Colour" onClear={selColors.length ? clearColors : null}>
          <ul className="space-y-2">
            {colors.map(([v, n]) => (
              <CheckRow key={v} checked={selColors.some((x) => x.toLowerCase() === v.toLowerCase())} onChange={() => onColor(v)} count={n} testid={`filter-color-${v}`}>
                <span className="w-3.5 h-3.5 rounded-full border border-black/10 shrink-0" style={{ background: swatchFor(v) }}/>{v}
              </CheckRow>
            ))}
          </ul>
        </Section>
      )}

      {fabrics.length > 0 && (
        <Section title="Fabric" onClear={selFabrics.length ? clearFabrics : null}>
          <ul className="space-y-2">
            {fabrics.map(([v, n]) => (
              <CheckRow key={v} checked={selFabrics.some((x) => x.toLowerCase() === v.toLowerCase())} onChange={() => onFabric(v)} count={n} testid={`filter-fabric-${v}`}>{v}</CheckRow>
            ))}
          </ul>
        </Section>
      )}

      {occasions.length > 0 && (
        <Section title="Occasion">
          <div className="flex flex-wrap gap-2">
            {occasions.map(([v]) => {
              const on = selOccasions.some((x) => x.toLowerCase() === v.toLowerCase());
              return <Pill key={v} on={on} onClick={() => onOccasion(v)}>{v}</Pill>;
            })}
          </div>
        </Section>
      )}

      <Section title="Tags">
        <div className="flex flex-wrap gap-2">
          {TAGS.map((t) => <Pill key={t.v} on={tags.includes(t.v)} onClick={() => onTag(t.v)} testid={`tag-${t.v}`}>{t.label}</Pill>)}
        </div>
      </Section>
    </div>
  );
}

function CatRow({ label, count, active, onClick, sub, testid }) {
  return (
    <li>
      <button onClick={onClick} data-testid={testid}
              className={`w-full flex items-center justify-between rounded-lg py-1.5 ${sub ? "pl-4 pr-2 text-[13px]" : "px-2"} transition-colors ${active ? "bg-[#E8F3EE] text-[#0E4D34] font-semibold" : "text-neutral-700 hover:bg-neutral-100"}`}>
        <span className="truncate">{sub ? "— " : ""}{label}</span>
        <span className={`text-xs ${active ? "text-[#0E4D34]" : "text-neutral-400"}`}>{count}</span>
      </button>
    </li>
  );
}

function CheckRow({ checked, onChange, count, children, testid }) {
  return (
    <li>
      <label className="flex items-center gap-2.5 cursor-pointer group" data-testid={testid}>
        <input type="checkbox" checked={checked} onChange={onChange} className="sr-only peer"/>
        <span className={`w-4 h-4 rounded-[5px] border flex items-center justify-center shrink-0 transition-colors ${checked ? "bg-[#0E4D34] border-[#0E4D34]" : "border-neutral-300 group-hover:border-neutral-500"} peer-focus-visible:ring-2 peer-focus-visible:ring-[#0E4D34]/40`}>
          {checked && <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-white"><path d="M2 6.5l2.5 2.5L10 3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </span>
        <span className="flex items-center gap-2 flex-1 min-w-0 text-neutral-700 truncate">{children}</span>
        <span className="text-xs text-neutral-400">{count}</span>
      </label>
    </li>
  );
}

function Pill({ on, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} aria-pressed={on}
            className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${on ? "bg-[#0E4D34] border-[#0E4D34] text-white" : "border-neutral-300 text-neutral-700 hover:border-[#0E4D34]"}`}>
      {children}{on && <X size={11} className="inline ml-1.5 -mt-0.5"/>}
    </button>
  );
}

/* Price bar (single handle, like the reference): drag left to set the maximum price */
function PriceRange({ bounds, value, onCommit }) {
  const [min, max] = bounds;
  const step = (max - min) > 20000 ? 500 : 100;
  const [v, setV] = React.useState(value);
  React.useEffect(() => setV(value), [value]);
  const timer = React.useRef(null);
  React.useEffect(() => () => clearTimeout(timer.current), []);
  const change = (n) => { setV(n); clearTimeout(timer.current); timer.current = setTimeout(() => onCommit(n), 300); };
  const pct = ((v - min) / (max - min || 1)) * 100;
  const active = v < max;
  return (
    <div data-testid="price-range">
      {active && <div className="text-xs text-neutral-700 mb-2">Up to <span className="font-semibold text-neutral-900">{inr(v)}</span></div>}
      <div className="relative h-5 price-bar">
        <div className="absolute top-1/2 -translate-y-1/2 inset-x-0 h-[2px] rounded-full bg-neutral-200"/>
        <div className="absolute top-1/2 -translate-y-1/2 left-0 h-[2px] rounded-full bg-neutral-900" style={{ width: `${pct}%` }}/>
        <input type="range" min={min} max={max} step={step} value={v} aria-label="Maximum price" data-testid="price-max"
               onChange={(e) => change(Number(e.target.value))}/>
      </div>
      <div className="flex justify-between text-[11px] text-neutral-500 mt-1.5">
        <span>{inr(min)}</span><span>{inr(max)}</span>
      </div>
    </div>
  );
}
